import React from 'react';

import { Box, Paper, Typography } from '@mui/material';

import { formatPrice } from '../../shared/checklistUtils';
import type { ManualApprovalState } from '../../types';

import DealFieldBox from './DealFieldBox';
import DealFieldLabel from './DealFieldLabel';
import InclusionRow, { INCLUSION_COLUMNS } from './InclusionRow';
import type { DealOptionsInclusionsDealRow } from './types';

interface Props {
  deal: DealOptionsInclusionsDealRow;
  manualApprovals: ManualApprovalState;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function DealCard({
  deal,
  manualApprovals,
  showAutoApproved,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  const visibleInclusions = deal.inclusions.filter((inc) => {
    if (showAutoApproved) {
      return true;
    }
    return inc.status !== 'approved' && !manualApprovals[inc.id];
  });

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', columnGap: 1.5, rowGap: 0.5 }}>
          <DealFieldLabel>DEAL NAME</DealFieldLabel>
          <DealFieldLabel>ROOM NAME</DealFieldLabel>
          <DealFieldLabel>SELL PRICE</DealFieldLabel>
          <DealFieldLabel>COST PRICE</DealFieldLabel>
          <DealFieldLabel>% MARGIN</DealFieldLabel>
          <DealFieldLabel>RACK RATE</DealFieldLabel>

          <DealFieldBox value={deal.dealOptionName || '—'} />
          <DealFieldBox value={deal.roomName || '—'} />
          <DealFieldBox value={formatPrice(deal.sellPrice, deal.currency)} />
          <DealFieldBox value={formatPrice(deal.costPrice, deal.currency)} />
          <DealFieldBox value={deal.marginPercent != null ? `${deal.marginPercent}%` : '—'} />
          <DealFieldBox value={formatPrice(deal.rackRate, deal.currency)} />
        </Box>
      </Box>

      {visibleInclusions.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {deal.inclusions.length === 0 ? 'No inclusions configured.' : 'All inclusions approved.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ width: 'max-content', minWidth: '100%' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: INCLUSION_COLUMNS,
                gap: 1.5,
                bgcolor: 'grey.50',
                borderBottom: 1,
                borderColor: 'divider',
                px: 2,
                py: 1,
              }}
            >
              <DealFieldLabel>Inclusion Name</DealFieldLabel>
              <DealFieldLabel>Inclusion Description</DealFieldLabel>
              <DealFieldLabel>Type</DealFieldLabel>
              <DealFieldLabel>Deal Option Inclusion RRP</DealFieldLabel>
              <DealFieldLabel>Inclusion Asset Value</DealFieldLabel>
              <DealFieldLabel>Total Value</DealFieldLabel>
              <DealFieldLabel>RRP Comparison</DealFieldLabel>
              <DealFieldLabel>Action</DealFieldLabel>
            </Box>
            {visibleInclusions.map((inc) => (
              <InclusionRow
                key={inc.id}
                currency={deal.currency}
                inc={inc}
                manuallyApproved={!!manualApprovals[inc.id]}
                onApprovalChange={onApprovalChange}
                onUnapprovalChange={onUnapprovalChange}
              />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
}
